import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useDebounce } from './useDebounce'
import {
  listAdminUsers,
  updateUserRole,
  activateUser,
  deactivateUser,
} from '@/api/adminUsers'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type {
  AdminUser,
  PaginatedAdminUsersResponse,
  UserRole,
} from '@/types/adminUser'

interface ApiErrorPayload {
  detail?: string
  title?: string
}

function getApiErrorMessage(
  error: AxiosError<ApiErrorPayload>,
  fallback: string
) {
  return error.response?.data?.detail ?? error.response?.data?.title ?? fallback
}

interface UseAdminUsersListParams {
  search: string
  role: UserRole | 'all'
  page: number
  pageSize: number
}

export function useAdminUsersList({
  search,
  role,
  page,
  pageSize,
}: UseAdminUsersListParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    role,
    page,
    pageSize,
  }

  return useQuery<PaginatedAdminUsersResponse>({
    queryKey: queryKeys.adminUsers.list(filters),
    queryFn: () =>
      listAdminUsers({
        search: debouncedSearch || undefined,
        role: role === 'all' ? undefined : role,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()

  return useMutation<
    AdminUser,
    AxiosError<ApiErrorPayload>,
    { userId: string; role: UserRole }
  >({
    mutationFn: ({ userId, role }) => updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all })
      toast.success('User role updated successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to update user role'))
    },
  })
}

export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation<AdminUser, AxiosError<ApiErrorPayload>, string>({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all })
      toast.success('User activated successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to activate user'))
    },
  })
}

export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation<AdminUser, AxiosError<ApiErrorPayload>, string>({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all })
      toast.success('User deactivated successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to deactivate user'))
    },
  })
}
